import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, ShieldCheck, Webhook, RefreshCw, Copy, CheckCircle2, XCircle,
  AlertTriangle, Clock, PlayCircle, ListChecks,
} from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { formatINR } from "./shared";

const fmtTime = (v) => {
  if (!v) return "—";
  // Razorpay capture time is a unix seconds int; audit "at" is ISO.
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const StatusPill = ({ ok, labelOk, labelBad }) => (
  <span className={`badge ${ok ? "badge-accepted" : "badge-rejected"} inline-flex items-center gap-1`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {ok ? labelOk : labelBad}
  </span>
);

function ModeCard({ mode, data, webhookUrl, onSelfTest, testing }) {
  const st = data?.last_selftest;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid={`payments-mode-${mode}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-xs uppercase tracking-widest text-gray-400">{mode} environment</div>
        <StatusPill ok={data?.keys_configured} labelOk="keys set" labelBad="no keys" />
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Key ID</span>
          <span className="font-mono text-gray-200" data-testid={`payments-key-${mode}`}>{data?.key_id_masked || "—"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Source</span>
          <span className="text-gray-300">{data?.source ? data.source.toUpperCase() : "—"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Webhook secret</span>
          <StatusPill ok={data?.webhook_configured} labelOk="configured" labelBad="missing" />
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-white/5">
        <button
          onClick={() => onSelfTest(mode)}
          disabled={testing}
          className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-2"
          data-testid={`webhook-selftest-btn-${mode}`}
        >
          {testing ? <span className="spinner" /> : <PlayCircle className="w-3.5 h-3.5" />} Run self-test
        </button>
        {st && (
          <div className="mt-2 text-xs" data-testid={`webhook-selftest-result-${mode}`}>
            <span className={`inline-flex items-center gap-1 ${st.ok ? "text-emerald-400" : "text-red-400"}`}>
              {st.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {st.ok ? "Signature valid" : (st.result || "failed")}
            </span>
            <span className="text-gray-500"> · HTTP {st.http_status ?? "—"} · {fmtTime(st.at)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaymentsPanel() {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [testing, setTesting] = useState("");

  const [window_, setWindow] = useState("7d");
  const [unrec, setUnrec] = useState(null);
  const [loadingUnrec, setLoadingUnrec] = useState(false);
  const [reconciling, setReconciling] = useState("");

  const [log, setLog] = useState([]);

  const [failedWin, setFailedWin] = useState("7d");
  const [failed, setFailed] = useState(null);
  const [loadingFailed, setLoadingFailed] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const { data } = await api.get("/admin/payments/health");
      setHealth(data);
    } catch (err) { toast.error(errorMessage(err)); } finally { setLoadingHealth(false); }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments/reconciliation-log");
      setLog(data.entries || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadUnrec = useCallback(async (w) => {
    setLoadingUnrec(true);
    try {
      const { data } = await api.get(`/admin/payments/unreconciled?window=${w}`);
      setUnrec(data);
      if (data.errors?.length) toast.error(`Razorpay error: ${data.errors[0].detail}`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setLoadingUnrec(false); }
  }, []);

  const loadFailed = useCallback(async (w) => {
    setLoadingFailed(true);
    try {
      const { data } = await api.get(`/admin/payments/failed?window=${w}`);
      setFailed(data);
    } catch { /* non-fatal */ } finally { setLoadingFailed(false); }
  }, []);

  useEffect(() => { loadHealth(); loadLog(); }, [loadHealth, loadLog]);
  useEffect(() => { loadUnrec(window_); }, [window_, loadUnrec]);
  useEffect(() => { loadFailed(failedWin); }, [failedWin, loadFailed]);

  const runSelfTest = async (mode) => {
    setTesting(mode);
    try {
      const { data } = await api.post("/admin/payments/webhook-selftest", { mode });
      if (data.ok) toast.success(`${mode.toUpperCase()} webhook self-test passed — signature valid.`);
      else if (data.result === "no_secret_configured") toast.error(data.message);
      else toast.error(`Self-test failed (${data.result}, HTTP ${data.http_status ?? "—"}).`);
      await loadHealth();
    } catch (err) { toast.error(errorMessage(err)); } finally { setTesting(""); }
  };

  const reconcile = async (row) => {
    if (!row.matchable) {
      toast.error("This payment has no matching IdeaThon account — flagged for manual review.");
      return;
    }
    if (!window.confirm(`Reconcile captured payment ${row.payment_id} (${formatINR(row.amount)})?\n\nThis marks the participant paid, issues their receipt and emails them. Idempotent — safe if already granted.`)) return;
    setReconciling(row.payment_id);
    try {
      const { data } = await api.post("/admin/reconcile-payment", { payment_id: row.payment_id });
      if (data.already_paid) toast.success(`${data.user_name} was already marked paid.`);
      else if (data.reconciled) toast.success(`Reconciled ${formatINR(data.amount)} for ${data.user_name}. Receipt emailed.`);
      else toast.message("Nothing changed.");
      await Promise.all([loadUnrec(window_), loadLog()]);
    } catch (err) { toast.error(errorMessage(err)); } finally { setReconciling(""); }
  };

  const copyUrl = () => {
    if (!health?.webhook_url) return;
    navigator.clipboard.writeText(health.webhook_url)
      .then(() => toast.success("Webhook URL copied")).catch(() => {});
  };

  const reasonBadge = (row) => {
    if (row.reason === "needs_review")
      return <span className="badge badge-invited inline-flex items-center gap-1" data-testid={`unrec-reason-${row.payment_id}`}><AlertTriangle className="w-3 h-3" /> needs review</span>;
    return <span className="badge badge-upcoming" data-testid={`unrec-reason-${row.payment_id}`}>captured · not granted</span>;
  };

  return (
    <div className="space-y-4" data-testid="payments-panel">
      {/* 1 + 2. Payment health summary + config status */}
      <div className="card-surface" data-testid="payments-health-card">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-brand" />
          <h3 className="text-white font-semibold">Payment health</h3>
          <button onClick={loadHealth} disabled={loadingHealth}
            className="ml-auto btn-secondary !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5" data-testid="payments-refresh-health">
            <RefreshCw className={`w-3.5 h-3.5 ${loadingHealth ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {loadingHealth && !health ? (
          <div className="py-10 flex justify-center"><span className="spinner" /></div>
        ) : health ? (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Active gateway</div>
                <div className="text-white mt-1 capitalize" data-testid="payments-active-gateway">{health.active_gateway}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Gateway mode</div>
                <div className="mt-1" data-testid="payments-mode-label">
                  <span className={`badge ${health.payment_mode === "live" ? "badge-accepted" : "badge-invited"} uppercase`}>{health.payment_mode}</span>
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Encryption</div>
                <div className="mt-1"><StatusPill ok={health.encryption_available} labelOk="available" labelBad="unavailable" /></div>
              </div>
            </div>

            {/* Webhook URL */}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 mb-4" data-testid="webhook-url-card">
              <div className="flex items-center gap-2 mb-2">
                <Webhook className="w-4 h-4 text-brand" />
                <span className="text-sm text-white font-medium">Webhook endpoint</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Paste this exact URL into Razorpay Dashboard → Settings → Webhooks (events: <span className="text-gray-300">payment.captured</span>, <span className="text-gray-300">order.paid</span>). Configure separately for Test and Live.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-gray-200 bg-black/30 rounded px-3 py-2 break-all" data-testid="webhook-url-value">{health.webhook_url}</code>
                <button onClick={copyUrl} className="btn-secondary !py-2 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="webhook-url-copy">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            </div>

            {/* Per-mode config + self-test */}
            <div className="grid sm:grid-cols-2 gap-3">
              <ModeCard mode="test" data={health.modes?.test} webhookUrl={health.webhook_url} onSelfTest={runSelfTest} testing={testing === "test"} />
              <ModeCard mode="live" data={health.modes?.live} webhookUrl={health.webhook_url} onSelfTest={runSelfTest} testing={testing === "live"} />
            </div>
          </>
        ) : (
          <p className="text-gray-500 text-sm">Could not load payment health.</p>
        )}
      </div>

      {/* 4. Unreconciled captured payments */}
      <div className="card-surface" data-testid="unreconciled-card">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-brand" />
          <h3 className="text-white font-semibold">Unreconciled captured payments</h3>
          <div className="ml-auto flex items-center gap-2">
            <select value={window_} onChange={(e) => setWindow(e.target.value)}
              className="input-field !py-1.5 !px-2 text-xs min-w-[120px]" data-testid="unreconciled-window">
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <button onClick={() => loadUnrec(window_)} disabled={loadingUnrec}
              className="btn-secondary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1.5" data-testid="unreconciled-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingUnrec ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Captured on Razorpay but not marked paid here. Scanned modes:{" "}
          <span className="text-gray-300">{(unrec?.modes_scanned || []).join(", ") || "—"}</span>
          {unrec ? <> · checked {unrec.checked} captured payment(s)</> : null}
        </p>

        {loadingUnrec ? (
          <div className="py-10 flex justify-center"><span className="spinner" /></div>
        ) : (unrec?.unreconciled?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-mono text-left border-b border-white/10">
                  <th className="py-2 pr-4">Payment / Order</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Captured</th>
                  <th className="py-2 pr-4">Env</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {unrec.unreconciled.map((r) => (
                  <tr key={r.payment_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`unrec-row-${r.payment_id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-mono text-xs text-gray-200">{r.payment_id}</div>
                      {r.order_id && <div className="font-mono text-[11px] text-gray-500">{r.order_id}</div>}
                    </td>
                    <td className="py-3 pr-4 text-white whitespace-nowrap">{formatINR(r.amount)} <span className="text-gray-600 text-xs">{r.currency}</span></td>
                    <td className="py-3 pr-4 text-gray-300">
                      <div>{r.email || "—"}</div>
                      {r.contact && <div className="text-xs text-gray-500">{r.contact}</div>}
                    </td>
                    <td className="py-3 pr-4 text-gray-400 whitespace-nowrap">{fmtTime(r.captured_at)}</td>
                    <td className="py-3 pr-4"><span className={`badge ${r.mode === "live" ? "badge-accepted" : "badge-invited"} uppercase text-[10px]`}>{r.mode}</span></td>
                    <td className="py-3 pr-4">{reasonBadge(r)}</td>
                    <td className="py-3 pr-4">
                      <button
                        onClick={() => reconcile(r)}
                        disabled={reconciling === r.payment_id || !r.matchable}
                        title={r.matchable ? "Reconcile & grant access" : "No matching account — manual review needed"}
                        className={`!py-1 !px-2.5 text-xs inline-flex items-center gap-1 ${r.matchable ? "btn-primary" : "btn-secondary opacity-60 cursor-not-allowed"}`}
                        data-testid={`unrec-reconcile-${r.payment_id}`}>
                        {reconciling === r.payment_id ? <span className="spinner" /> : <RefreshCw className="w-3 h-3" />}
                        {r.matchable ? "Reconcile" : "Review"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 text-sm" data-testid="unreconciled-empty">
            <CheckCircle2 className="w-5 h-5 mx-auto mb-2 text-emerald-400" />
            No unreconciled captured payments in this window. Webhooks are keeping up.
          </div>
        ))}
      </div>

      {/* Recent failed attempts (from payment.failed webhooks) */}
      <div className="card-surface" data-testid="failed-payments-card">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-brand" />
          <h3 className="text-white font-semibold">Recent failed attempts</h3>
          <div className="ml-auto flex items-center gap-2">
            <select value={failedWin} onChange={(e) => setFailedWin(e.target.value)}
              className="input-field !py-1.5 !px-2 text-xs min-w-[120px]" data-testid="failed-window">
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <button onClick={() => loadFailed(failedWin)} disabled={loadingFailed}
              className="btn-secondary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1.5" data-testid="failed-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingFailed ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Declined / failed payment attempts reported by Razorpay (payer contact + real error reason). Populates only when the webhook is configured with the <span className="text-gray-300">payment.failed</span> event.
        </p>
        {loadingFailed ? (
          <div className="py-10 flex justify-center"><span className="spinner" /></div>
        ) : (failed?.failed?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-mono text-left border-b border-white/10">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Payer</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Method</th>
                  <th className="py-2 pr-4">Why it failed</th>
                  <th className="py-2 pr-4">Env</th>
                </tr>
              </thead>
              <tbody>
                {failed.failed.map((f) => (
                  <tr key={f.payment_id} className="border-b border-white/5" data-testid={`failed-row-${f.payment_id}`}>
                    <td className="py-3 pr-4 text-gray-400 whitespace-nowrap">{fmtTime(f.failed_at)}</td>
                    <td className="py-3 pr-4 text-gray-300">
                      <div>{f.email || "—"}</div>
                      {f.contact && <div className="text-xs text-gray-500">{f.contact}</div>}
                      {f.vpa && <div className="text-[11px] font-mono text-gray-600">{f.vpa}</div>}
                    </td>
                    <td className="py-3 pr-4 text-white whitespace-nowrap">{formatINR(f.amount)}</td>
                    <td className="py-3 pr-4 text-gray-400 uppercase text-xs">{f.method || "—"}</td>
                    <td className="py-3 pr-4 text-gray-300 max-w-[280px]">
                      <div className="text-red-400">{f.error_description || f.error_reason || "Declined"}</div>
                      {f.error_reason && f.error_description && f.error_reason !== f.error_description && (
                        <div className="text-[11px] font-mono text-gray-600 mt-0.5">{f.error_reason}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4"><span className={`badge ${f.mode === "live" ? "badge-accepted" : "badge-invited"} uppercase text-[10px]`}>{f.mode || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-6 text-center text-gray-500 text-sm" data-testid="failed-empty">No failed attempts logged in this window.</p>
        ))}
      </div>

      {/* 5. Recent reconciliation activity */}
      <div className="card-surface" data-testid="recon-log-card">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-brand" />
          <h3 className="text-white font-semibold">Recent reconciliation activity</h3>
          <button onClick={loadLog} className="ml-auto btn-secondary !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5" data-testid="recon-log-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {log.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-mono text-left border-b border-white/10">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Admin</th>
                  <th className="py-2 pr-4">Payment</th>
                  <th className="py-2 pr-4">State change</th>
                  <th className="py-2 pr-4">Amount</th>
                </tr>
              </thead>
              <tbody>
                {log.map((e) => (
                  <tr key={e.id} className="border-b border-white/5" data-testid={`recon-log-row-${e.id}`}>
                    <td className="py-2.5 pr-4 text-gray-400 whitespace-nowrap">{fmtTime(e.at)}</td>
                    <td className="py-2.5 pr-4 text-gray-300">{e.admin_email || "—"}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-gray-200">{e.payment_id}</td>
                    <td className="py-2.5 pr-4">
                      <span className="text-gray-500">{e.previous_state}</span>
                      <span className="text-gray-600"> → </span>
                      <span className={e.granted ? "text-emerald-400" : "text-gray-300"}>{e.resulting_state}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-white">{e.amount != null ? formatINR(e.amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-6 text-center text-gray-500 text-sm" data-testid="recon-log-empty">No reconciliation activity yet.</p>
        )}
      </div>
    </div>
  );
}
